import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
const NAV_ITEMS = [{
  to: '/terms',
  label: 'Terms of Service',
  sections: [{
    number: '1',
    title: 'Agreement to These Terms'
  }, {
    number: '2',
    title: 'Enterprise Agreements; Order of Precedence'
  }, {
    number: '3',
    title: 'Eligibility'
  }, {
    number: '4',
    title: 'Accounts and Security'
  }, {
    number: '5',
    title: 'Privacy'
  }, {
    number: '6',
    title: 'The Services; Plans; Use on Behalf of Clients'
  }, {
    number: '7',
    title: 'Your Content'
  }, {
    number: '8',
    title: 'AI Features and AI Output'
  }, {
    number: '9',
    title: 'Publishing, Embeds, and Your Sites'
  }, {
    number: '10',
    title: 'Acceptable Use'
  }, {
    number: '11',
    title: 'Fees and Billing (Self-Serve Plans)'
  }, {
    number: '12',
    title: "Maker's Intellectual Property"
  }, {
    number: '13',
    title: 'Feedback'
  }, {
    number: '14',
    title: 'Third-Party Services and Links'
  }, {
    number: '15',
    title: 'Copyright Policy (DMCA)'
  }, {
    number: '16',
    title: 'Suspension and Termination'
  }, {
    number: '17',
    title: 'Changes to the Services'
  }, {
    number: '18',
    title: 'Beta and Pilot Features'
  }, {
    number: '19',
    title: 'Disclaimer of Warranties'
  }, {
    number: '20',
    title: 'Limitation of Liability'
  }, {
    number: '21',
    title: 'Indemnification'
  }, {
    number: '22',
    title: 'Export Controls and Sanctions'
  }, {
    number: '23',
    title: 'Governing Law; Venue; Time to Bring Claims'
  }, {
    number: '24',
    title: 'Changes to These Terms'
  }, {
    number: '25',
    title: 'General Terms'
  }, {
    number: '26',
    title: 'Contact and Reporting'
  }]
}];
export default function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('');
  const [expandedPages, setExpandedPages] = useState<Record<string, boolean>>({
    '/terms': true
  });
  useEffect(() => {
    if (location.pathname) {
      setExpandedPages(prev => ({
        ...prev,
        [location.pathname]: true
      }));
    }
  }, [location.pathname]);
  const togglePageExpand = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedPages(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
        setActiveSection(sectionId);
        return;
      }
    }
    window.scrollTo(0, 0);
    setActiveSection('');
  }, [location.pathname, location.hash]);
  useEffect(() => {
    const sections = Array.from(document.querySelectorAll<HTMLElement>('section[id^="section-"]'));
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(entries => {
      const visibleEntry = entries.find(entry => entry.isIntersecting);
      if (visibleEntry) {
        setActiveSection(visibleEntry.target.id);
      }
    }, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0.1
    });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [location.pathname]);
  const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, pagePath: string, sectionNumber: string) => {
    e.preventDefault();
    const sectionId = `section-${sectionNumber}`;
    if (location.pathname === pagePath) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        setActiveSection(sectionId);
      }
    } else {
      navigate(`${pagePath}#${sectionId}`);
    }
  };
  return <div className="min-h-screen flex flex-col bg-white text-slate-800">
      <div className="flex-1 max-w-6xl w-full mx-auto px-6 py-10 md:py-14 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-10">
        <nav className="md:sticky md:top-12 md:self-start md:max-h-[calc(100vh-6rem)] md:overflow-y-auto pr-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
            Terms &amp; Policies
          </p>
          <ul className="space-y-4 text-sm">

            {NAV_ITEMS.map(item => {
            const isPageActive = location.pathname === item.to;
            const isExpanded = expandedPages[item.to] ?? isPageActive;
            return <li key={item.to} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Link to={item.to} className={`flex-1 block rounded-md px-2.5 py-1.5 font-medium transition-colors ${isPageActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-tracking-id={`nav-${item.to}`}>
                      {item.label}
                    </Link>
                    {item.sections.length > 0 && <button type="button" onClick={e => togglePageExpand(item.to, e)} className="p-1.5 text-slate-400 hover:text-slate-700 rounded transition-colors" aria-label={`Toggle ${item.label} section index`} data-tracking-id={`toggle-${item.to}`}>
                        <svg className={`w-3.5 h-3.5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>}
                  </div>
                  {isExpanded && item.sections.length > 0 && <ul className="ml-3.5 pl-3 border-l border-slate-200 space-y-1 text-xs max-h-56 overflow-y-auto my-1.5 py-1">
                      {item.sections.map(section => {
                  const sectionId = `section-${section.number}`;
                  const isSectionActive = activeSection === sectionId || location.hash === `#${sectionId}`;
                  return <li key={section.number}>
                            <a href={`${item.to}#${sectionId}`} onClick={e => handleSectionClick(e, item.to, section.number)} className={`block py-1 px-1.5 rounded transition-colors ${isSectionActive ? 'text-slate-900 font-medium bg-slate-100/80' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`} data-tracking-id={`nav-section-${section.number}`}>
                              {section.title}
                            </a>
                          </li>;
                })}
                    </ul>}
                </li>;
          })}
          </ul>
        </nav>

        <main className="min-w-0">{children}</main>
      </div>
    </div>;
}